import type { SupportedLanguages } from '../i18n';

interface GooglePlacesPeriod {
  open: {
    day: number;
    time: string;
  };
  close?: {
    day: number;
    time: string;
  };
}

interface GooglePlacesOpeningHours {
  periods?: GooglePlacesPeriod[];
  timezone?: string;
}

const DAY_TRANSLATIONS: Record<string, Record<SupportedLanguages, string>> = {
  'monday': {
    fr: 'Lundi',
    en: 'Monday',
    es: 'Lunes',
    it: 'Lunedì',
    pt: 'Segunda-feira',
    de: 'Montag',
    nl: 'Maandag',
    ja: '月曜日',
    zh: '星期一',
    ru: 'Понедельник'
  },
  'tuesday': {
    fr: 'Mardi',
    en: 'Tuesday',
    es: 'Martes',
    it: 'Martedì',
    pt: 'Terça-feira',
    de: 'Dienstag',
    nl: 'Dinsdag',
    ja: '火曜日',
    zh: '星期二',
    ru: 'Вторник'
  },
  'wednesday': {
    fr: 'Mercredi',
    en: 'Wednesday',
    es: 'Miércoles',
    it: 'Mercoledì',
    pt: 'Quarta-feira',
    de: 'Mittwoch',
    nl: 'Woensdag',
    ja: '水曜日',
    zh: '星期三',
    ru: 'Среда'
  },
  'thursday': {
    fr: 'Jeudi',
    en: 'Thursday',
    es: 'Jueves',
    it: 'Giovedì',
    pt: 'Quinta-feira',
    de: 'Donnerstag',
    nl: 'Donderdag',
    ja: '木曜日',
    zh: '星期四',
    ru: 'Четверг'
  },
  'friday': {
    fr: 'Vendredi',
    en: 'Friday',
    es: 'Viernes',
    it: 'Venerdì',
    pt: 'Sexta-feira',
    de: 'Freitag',
    nl: 'Vrijdag',
    ja: '金曜日',
    zh: '星期五',
    ru: 'Пятница'
  },
  'saturday': {
    fr: 'Samedi',
    en: 'Saturday',
    es: 'Sábado',
    it: 'Sabato',
    pt: 'Sábado',
    de: 'Samstag',
    nl: 'Zaterdag',
    ja: '土曜日',
    zh: '星期六',
    ru: 'Суббота'
  },
  'sunday': {
    fr: 'Dimanche',
    en: 'Sunday',
    es: 'Domingo',
    it: 'Domenica',
    pt: 'Domingo',
    de: 'Sonntag',
    nl: 'Zondag',
    ja: '日曜日',
    zh: '星期日',
    ru: 'Воскресенье'
  }
};

const CLOSED_TRANSLATIONS: Record<SupportedLanguages, string> = {
  fr: 'Fermé',
  en: 'Closed',
  es: 'Cerrado',
  it: 'Chiuso',
  pt: 'Fechado',
  de: 'Geschlossen',
  nl: 'Gesloten',
  ja: '休業',
  zh: '休息',
  ru: 'Закрыто'
};

interface ParsedHours {
  day: string;
  hours: string;
}

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function formatTime(time: string, language: SupportedLanguages = 'en'): string {
  if (time.length === 4) {
    const hours = parseInt(time.slice(0, 2));
    const minutes = time.slice(2);

    const use12Hour = ['en'].includes(language);

    if (use12Hour) {
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHours}:${minutes} ${period}`;
    }

    return `${time.slice(0, 2)}:${minutes}`;
  }
  return time;
}

function convertGooglePlacesToText(googleHours: GooglePlacesOpeningHours, language: SupportedLanguages = 'en'): string {
  if (!googleHours.periods || googleHours.periods.length === 0) {
    return '';
  }

  const daySchedules: Record<number, string[]> = {};

  googleHours.periods.forEach(period => {
    const day = period.open.day;
    const openTime = formatTime(period.open.time, language);
    const closeTime = period.close ? formatTime(period.close.time, language) : '';

    if (!daySchedules[day]) {
      daySchedules[day] = [];
    }

    if (closeTime) {
      daySchedules[day].push(`${openTime} - ${closeTime}`);
    } else {
      daySchedules[day].push(`${openTime}`);
    }
  });

  const lines: string[] = [];
  for (let i = 0; i < 7; i++) {
    if (daySchedules[i]) {
      const dayName = DAY_NAMES[i];
      const times = daySchedules[i].join(', ');
      lines.push(`${dayName}: ${times}`);
    } else {
      const dayName = DAY_NAMES[i];
      lines.push(`${dayName}: Closed`);
    }
  }

  return lines.join('\n');
}

export function parseOpeningHours(openingHours: string | null | undefined): ParsedHours[] {
  if (!openingHours || typeof openingHours !== 'string' || openingHours.trim() === '') {
    return [];
  }

  const lines = openingHours.split('\n').filter(line => line.trim() !== '');
  const parsed: ParsedHours[] = [];

  for (const line of lines) {
    const match = line.match(/^([^:]+):\s*(.+)$/);
    if (match) {
      const day = match[1].trim().toLowerCase();
      const hours = match[2].trim();
      parsed.push({ day, hours });
    }
  }

  return parsed;
}

export function formatOpeningHours(openingHours: string | null | undefined | any, language: SupportedLanguages): string {
  if (!openingHours) {
    return '';
  }

  let textHours: string;

  if (typeof openingHours === 'object' && openingHours.periods) {
    textHours = convertGooglePlacesToText(openingHours as GooglePlacesOpeningHours, language);
  } else if (typeof openingHours === 'string') {
    textHours = openingHours;
  } else {
    return '';
  }

  const parsed = parseOpeningHours(textHours);

  if (parsed.length === 0) {
    return '';
  }

  return parsed
    .map(({ day, hours }) => {
      const translatedDay = DAY_TRANSLATIONS[day]?.[language] || day;

      const translatedHours = hours.toLowerCase() === 'closed'
        ? CLOSED_TRANSLATIONS[language]
        : hours;

      return `${translatedDay}: ${translatedHours}`;
    })
    .join('\n');
}

export function isOpenNow(openingHours: string | null | undefined | any): boolean {
  if (!openingHours) {
    return false;
  }

  let textHours: string;

  if (typeof openingHours === 'object' && openingHours.periods) {
    textHours = convertGooglePlacesToText(openingHours as GooglePlacesOpeningHours, 'en');
  } else if (typeof openingHours === 'string') {
    if (openingHours.trim() === '') {
      return false;
    }
    textHours = openingHours;
  } else {
    return false;
  }

  const now = new Date();
  const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  const currentTime = now.getHours() * 60 + now.getMinutes();

  const parsed = parseOpeningHours(textHours);
  const todayHours = parsed.find(({ day }) => day === currentDay);

  if (!todayHours || todayHours.hours.toLowerCase() === 'closed') {
    return false;
  }

  const timeMatch = todayHours.hours.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?\s*[–-]\s*(\d{1,2}):(\d{2})\s*(AM|PM)?/i);

  if (!timeMatch) {
    return false;
  }

  let openHour = parseInt(timeMatch[1]);
  const openMin = parseInt(timeMatch[2]);
  const openPeriod = timeMatch[3]?.toUpperCase();

  let closeHour = parseInt(timeMatch[4]);
  const closeMin = parseInt(timeMatch[5]);
  const closePeriod = timeMatch[6]?.toUpperCase();

  if (openPeriod === 'PM' && openHour !== 12) openHour += 12;
  if (openPeriod === 'AM' && openHour === 12) openHour = 0;

  if (closePeriod === 'PM' && closeHour !== 12) closeHour += 12;
  if (closePeriod === 'AM' && closeHour === 12) closeHour = 0;

  const openTime = openHour * 60 + openMin;
  const closeTime = closeHour * 60 + closeMin;

  return currentTime >= openTime && currentTime < closeTime;
}
